import { ClientService } from '@services/index'
import i18n from '@utils/i18n'
import { isValidEmail, isValidUsername } from '@utils/validators/string.validator'

type ValidationResponse = {
  isValid: boolean
  errorMsg?: string
}

/**
 * Validates the given email for use during the sign up process
 * @param email
 */
export const validateEmail = async (email?: string): Promise<ValidationResponse> => {
  email = email?.trim()
  if (!email) {
    return { isValid: false, errorMsg: i18n.t('errors.missingEmailAddress') }
  }

  if (!isValidEmail(email)) {
    return { isValid: false, errorMsg: i18n.t('errors.invalidEmail') }
  }

  const resp = await ClientService.checkEmail(email)
  if (!resp) {
    return { isValid: false, errorMsg: i18n.t('errors.couldNotCheckEmail') }
  }

  if (resp.email_taken) {
    return { isValid: false, errorMsg: i18n.t('errors.emailAlreadyExists') }
  }

  if (resp.invalid_chars) {
    return { isValid: false, errorMsg: i18n.t('errors.emailContainsInvalidCharacters') }
  }

  return { isValid: true }
}

/**
 * Validates the given username for use during the sign up process
 * @param username
 */
export const validateUsername = async (username?: string): Promise<ValidationResponse> => {
  username = username?.trim()
  if (!username) {
    return { isValid: false, errorMsg: i18n.t('errors.usernameRequired') }
  }

  if (!isValidUsername(username)) {
    return { isValid: false, errorMsg: i18n.t('errors.usernameFormat') }
  }

  const resp = await ClientService.checkUsername(username)
  if (!resp) {
    return { isValid: false, errorMsg: i18n.t('errors.couldNotCheckUsername') }
  }

  if (resp.username_taken) {
    return { isValid: false, errorMsg: i18n.t('errors.usernameAlreadyExists') }
  }

  if (resp.invalid_chars) {
    return { isValid: false, errorMsg: i18n.t('errors.usernameFormat') }
  }

  return { isValid: true }
}

export const MIN_PASSWORD_LENGTH = 4
export const MAX_PASSWORD_LENGTH = 200

/**
 * Validates the given password for use during the sign up process
 * @param password
 */
export const validatePassword = (password?: string): ValidationResponse => {
  password = password?.trim()
  if (!password) {
    return { isValid: false, errorMsg: i18n.t('errors.passwordRequired') }
  }

  if (password.length < MIN_PASSWORD_LENGTH) {
    return {
      isValid: false,
      errorMsg: i18n.t('errors.passwordTooShort', { min: MIN_PASSWORD_LENGTH }),
    }
  }

  if (password.length > MAX_PASSWORD_LENGTH) {
    return {
      isValid: false,
      errorMsg: i18n.t('errors.passwordTooLong', { max: MAX_PASSWORD_LENGTH }),
    }
  }

  return { isValid: true }
}

/**
 * Validates the given age for use during the sign up process
 * @param age
 */
export const validateAge = (age?: number): ValidationResponse => {
  if (!age) {
    return { isValid: false, errorMsg: i18n.t('errors.required') }
  }

  if (isNaN(Number(age))) {
    return { isValid: false, errorMsg: i18n.t('errors.onlyNumbers') }
  }

  if (age < 13) {
    return { isValid: false, errorMsg: i18n.t('content.signup.ageInfo') }
  }

  return { isValid: true }
}
